import { useState, useEffect } from 'react'
import {
  Badge,
  Tooltip,
  makeStyles,
} from '@fluentui/react-components'
import { api, type QueueStatus as QueueData } from '../lib/api'
import { useConfig } from '../context/ConfigContext'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  badge: {
    cursor: 'default',
  },
})

export function QueueStatus() {
  const styles = useStyles()
  const { config } = useConfig()
  const [queue, setQueue] = useState<QueueData | null>(null)

  useEffect(() => {
    if (!config?.integration.showQueueCount) return

    const fetchQueue = async () => {
      try {
        const data = await api.getQueueStatus()
        setQueue(data)
      } catch (err) {
        console.error('Queue fetch error:', err)
      }
    }

    fetchQueue()
    // Refresh every 5 minutes
    const interval = setInterval(fetchQueue, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [config])

  if (!queue?.enabled || queue.companyActiveTickets === undefined) return null

  const getBadgeColor = (level?: string): 'success' | 'warning' | 'danger' | 'informative' => {
    switch (level) {
      case 'low': return 'success'
      case 'normal': return 'informative'
      case 'busy': return 'warning'
      case 'very_busy': return 'danger'
      default: return 'informative'
    }
  }

  const getLabel = (): string => {
    const count = queue.companyActiveTickets ?? 0
    if (count === 0) return 'No active tickets'
    const ahead = queue.aheadInQueue ?? 0
    if (ahead === 0) return `${count} active · You're next`
    return `${count} active · ${ahead} ahead`
  }

  return (
    <div className={styles.root}>
      <Tooltip
        content={queue.statusMessage || `${queue.companyActiveTickets} active tickets from your company`}
        relationship="description"
      >
        <Badge
          appearance="filled"
          color={getBadgeColor(queue.queueLevel)}
          className={styles.badge}
        >
          {getLabel()}
        </Badge>
      </Tooltip>
    </div>
  )
}
